/**
 * Pure builders for the workflow API tab (components/workflow/ApiPanel.jsx) — what a
 * caller should send and what they'll get back. No React, so vitest covers them
 * directly (tests/unit/workflowApiExamples.test.js).
 */

/** Realistic sample for one input field — a `0` reads as "nothing here" and would pass
 *  silently through engines that skip falsy values, so the sample should be a value the
 *  caller could actually send. Keyed by measurement names (the tailoring workflow's
 *  contract, all inches per its field descriptions); anything unmatched falls back to a
 *  type placeholder. An `options` (string enum) field samples its first allowed value —
 *  a sample outside the enum would be rejected by the run-time options check. */
const NAME_SAMPLES = {
  chest: 36,
  waist: 30,
  shoulder: 14,
  blouse_length: 22,
  sleeve_length: 8,
  sleeve_round: 12,
  armhole: 16,
  neck_depth_front: 7,
  neck_depth_back: 2.5,
  fabric_width: 45,
  style_notes: 'Round neck, front button placket, elbow-length sleeves, waist darts',
};

function sampleFor(field) {
  if (field.type === 'string' && Array.isArray(field.options) && field.options.length) return field.options[0];
  if (Object.prototype.hasOwnProperty.call(NAME_SAMPLES, field.name)) return NAME_SAMPLES[field.name];
  if (field.type === 'number') return 0;
  if (field.type === 'boolean') return false;
  return '…';
}

/**
 * The `input` object a caller should send, generated from the workflow's input schema
 * ({name,type,description,optional,options}). Unnamed (still-being-typed) fields are
 * skipped, matching workflowPayload's save-side filter; a schema-less workflow calls
 * for {}. Realistic samples where the field is known (see sampleFor), placeholders
 * otherwise — this prefills the Execute section's body and the docs payload example.
 */
export function buildInputExample(fields) {
  const input = {};
  for (const f of Array.isArray(fields) ? fields : []) {
    if (f && f.name && f.name.trim()) input[f.name.trim()] = sampleFor(f);
  }
  return input;
}

/**
 * The full run-body example for the Execute section: `input` plus — for LLM-app
 * workflows (a spec_json with kind "llm_app") — the four per-run spec fields prefilled
 * from the saved spec. ALL FOUR keys are always present: the run endpoint treats an
 * absent/null field as "not sent" and rejects a partial override set, so an example
 * missing one key would teach callers a body the server refuses. Agent-mode specs keep
 * all four too (the server ignores system_prompt for a specialist turn).
 *
 * No/invalid spec parses to the plain `{input}` body a non-LLM-app workflow takes.
 */
export function buildRunBodyExample(fields, specJson) {
  const body = { input: buildInputExample(fields) };
  let spec = null;
  try { spec = JSON.parse(specJson || 'null'); } catch { spec = null; }
  if (!spec || spec.kind !== 'llm_app') return body;
  return {
    ...body,
    system_prompt: typeof spec.system_prompt === 'string' ? spec.system_prompt : '',
    user_prompt: spec.user_prompt || '',
    output_schema: Array.isArray(spec.output) ? spec.output : [],
    client_tools: Array.isArray(spec.client_tools) ? spec.client_tools : [],
  };
}

/**
 * How `run.output` will look once the run finishes (engine.js's Output node):
 *  - `template`   — the Output node has a template, so the caller gets the rendered
 *                   string/value; show it via a `${{...}}` placeholder.
 *  - `scope`      — empty template (the common case): every node's output keyed by node
 *                   id, one level deep inside `output.output`.
 *  - `none`       — no Output node reached; the run ends in error/exit, not `done`.
 */
export function outputShapeFor(graph) {
  const outputNode = ((graph && graph.nodes) || []).find((n) => n.type === 'output');
  if (!outputNode) return { mode: 'none' };
  const template = (outputNode.data && outputNode.data.template) || '';
  return template.trim() ? { mode: 'template', template } : { mode: 'scope' };
}

/** Indented JSON string for the curl/pre blocks. */
export function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

/**
 * The cURL for POST /api/v1/workflows/{id_or_key}/run — shared by the docs blocks and
 * the Execute section so a copied command is byte-identical to the call the UI makes.
 * `token` falls back to the `tnt_…` placeholder when no credential is at hand;
 * `wait` > 0 adds the bounded long-poll query and folds the command onto one line.
 */
export function buildRunCurl({ runUrl, body, token, wait = 0 }) {
  const auth = `Authorization: Bearer ${token || 'tnt_…'}`;
  const esc = (s) => s.replace(/'/g, "'\\''");
  if (wait > 0) {
    return `curl -X POST "${runUrl}?wait=${wait}" \\\n  -H "${auth}" -H 'Content-Type: application/json' -d '${esc(body)}'`;
  }
  return `curl -X POST "${runUrl}" \\\n  -H "${auth}" \\\n  -H 'Content-Type: application/json' \\\n  -d '${esc(body)}'`;
}

/**
 * JavaScript (fetch) code snippet for calling the run endpoint.
 */
export function buildRunJsFetch({ runUrl, body, token, wait = 0 }) {
  const endpoint = wait > 0 ? `${runUrl}?wait=${wait}` : runUrl;
  const auth = `Bearer ${token || 'tnt_…'}`;
  return `const response = await fetch('${endpoint}', {
  method: 'POST',
  headers: {
    'Authorization': '${auth}',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify(${body.trim()}),
});
const data = await response.json();
console.log(data);`;
}

/**
 * Python (requests) code snippet for calling the run endpoint.
 */
export function buildRunPythonRequests({ runUrl, body, token, wait = 0 }) {
  const endpoint = wait > 0 ? `${runUrl}?wait=${wait}` : runUrl;
  const auth = `Bearer ${token || 'tnt_…'}`;
  return `import requests

url = "${endpoint}"
headers = {
    "Authorization": "${auth}",
    "Content-Type": "application/json"
}
data = """${body.trim()}"""

response = requests.post(url, data=data, headers=headers)
print(response.json())`;
}

